// {{WORKTREE_ROOT}}/picosponsorconsole/app/routes/signin.sso._index.tsx
// PLAN2 WP35 §9.6 (D35-14, C-49) — /signin/sso: the "Sign in with single sign-on" page. A work email whose domain has single sign-on goes straight to the identity provider; any other address is told so plainly
// (this page is the place that says it — /signin itself answers the same for known and unknown addresses).
import { json, type ActionFunctionArgs, type LoaderFunctionArgs } from "@remix-run/node";
import { Form, Link, useActionData, useLoaderData, useNavigation } from "@remix-run/react";
import { Button, TextField } from "@picosponsor/design";
import { AuthFrame } from "~/components/auth/AuthFrame";
import { CsrfInput } from "~/components/common/CsrfInput";
import { useT } from "~/utils/locale";
import { TESTIDS } from "~/utils/testids";
import { assertHost } from "~/utils/host.server";
import { env } from "~/utils/env.server";
import { handleAction } from "~/utils/action.server";
import { failure, type ActionFailure } from "~/utils/actionResult";
import { authClient } from "~/utils/cognito.server";
import { startSso } from "~/utils/ssoState.server";
import { failureText } from "~/utils/failureText";
import { pageTitle } from "~/utils/pageTitle";
import { sanitizeNext } from "~/utils/next";
import { EMAIL_RE } from "./signin._index";

export const meta = pageTitle("auth", "sso-title");

export async function loader({ request }: LoaderFunctionArgs) {
  assertHost(request, ["app", "room"]);
  return json({ next: sanitizeNext(new URL(request.url).searchParams.get("next")), region: env().regionCode });
}

export async function action({ request }: ActionFunctionArgs) {
  return handleAction(request, { session: "none" }, async ({ form }) => {
    const host = assertHost(request, ["app", "room"]);
    const email = String(form.get("email") ?? "").trim().toLowerCase();
    if (!EMAIL_RE.test(email)) return json(failure("VALIDATION", "auth.error-email-invalid", undefined, { email: "auth.error-email-invalid" }), { status: 400 });
    const found = await authClient.ssoDiscover({ email, host: host === "room" ? "room" : "app" });
    if (found.mode === "none") return json(failure("VALIDATION", "auth.sso-none"), { status: 400 });
    if (found.mode === "blocked") return json(failure("FORBIDDEN_ROLE", "auth.sso-blocked"), { status: 403 });
    return startSso(request, email, sanitizeNext(String(form.get("next") ?? "")));
  });
}

export default function SignInSso() {
  const t = useT();
  const data = useLoaderData<typeof loader>();
  const result = useActionData<ActionFailure>();
  const nav = useNavigation();
  const error = result && result.ok === false ? failureText(t, result.error) : undefined;
  return (
    <AuthFrame regionLine={t("auth", "region-line", { region: t("common", `region-${data.region}`) })} testId={TESTIDS.auth.sso.page}>
      <h1 className="display m-0 text-30 leading-0.95 tracking-tight-30 cq-md:text-32">{t("auth", "sso-title")}</h1>
      <p className="m-0 text-14 text-dim">{t("auth", "sso-lede")}</p>
      <Form method="post" className="flex flex-col gap-4">
        <CsrfInput />
        <input type="hidden" name="next" value={data.next} />
        <TextField
          // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: the work email is this page's only task
          label={t("auth", "email-label")} hideLabel name="email" type="email" inputMode="email" required autoFocus autoComplete="username"
          placeholder={t("placeholders", "email")} error={error} data-testid={TESTIDS.auth.sso.email} />
        <Button type="submit" variant="primary" size="l" className="w-full" loading={nav.state !== "idle"} data-testid={TESTIDS.auth.sso.submit}>{t("auth", "sso-continue")}</Button>
      </Form>
      {result && result.ok === false ? <Link to="/signin" className="text-14 font-bold text-accent" data-testid={TESTIDS.auth.sso.error}>{t("auth", "signin-title")}</Link> : null}
    </AuthFrame>
  );
}
